import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { SKILL_LEVELS } from '../../constants';
import VerificationMessage from './VerificationMessage';
import { useNavigate } from 'react-router-dom';

const AuthForm = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    skillLevel: '2.5'
  });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const { signIn, signUp, loading } = useAuth();

  const validateForm = () => {
    const newErrors = {};

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    // Registration-specific validation
    if (!isLogin) {
      if (!formData.firstName) {
        newErrors.firstName = 'First name is required';
      }
      if (!formData.lastName) {
        newErrors.lastName = 'Last initial is required';
      }
      if (!formData.phoneNumber) {
        newErrors.phoneNumber = 'Phone number is required';
      } else if (!/^\+?[\d\s-]{10,}$/.test(formData.phoneNumber)) {
        newErrors.phoneNumber = 'Please enter a valid phone number';
      }
      if (!formData.skillLevel) {
        newErrors.skillLevel = 'Skill level is required';
      }
      if (!acceptedTerms) {
        newErrors.terms = 'You must agree to the Terms and Conditions';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    setErrors({});

    try {
      let result;
      
      if (isLogin) {
        console.log('Attempting sign in with:', formData.email);
        result = await signIn(formData.email, formData.password);
        console.log('Sign in result:', result);
        
        // Show verification message if email is not confirmed
        if (result.error?.message?.includes('verify your email')) {
          console.log('Email needs verification, showing message');
          setVerificationEmail(formData.email);
          return;
        }

        // If sign in was successful, redirect to dashboard
        if (!result.error) {
          navigate('/');
          return;
        }
      } else {
        result = await signUp(
          formData.email,
          formData.password,
          formData.firstName,
          formData.lastName,
          formData.skillLevel,
          formData.phoneNumber
        );

        // Always show verification message after signup
        if (!result.error) {
          setVerificationEmail(formData.email);
          return;
        }
      }

      if (result.error) {
        console.log('Setting error:', result.error.message);
        setErrors({ submit: result.error.message });
      }
    } catch (error) {
      console.error('Submit error:', error);
      setErrors({ submit: 'An unexpected error occurred. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setErrors({});
    setAcceptedTerms(false);
    setFormData({
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      phoneNumber: '',
      skillLevel: '2.5'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F8F8]">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (verificationEmail) {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center bg-[#F8F8F8] px-4 pb-24">
        <div className="w-full max-w-md">
          {/* Logo/Header Section - Updated to match main page */}
          <header className="bg-primary shadow-sm mb-8 rounded-lg">
            <div className="px-6 py-8 text-center">
              <div className="flex flex-col items-center space-y-2">
                <div className="flex items-center gap-3">
                                  <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight text-white">
                  Tennis<span className="text-accent">Now</span><span className="text-white">App.com</span>
                </h1>
                </div>
                <p className="text-white/80 text-lg font-light">Connecting the Triangle's Tennis Community</p>
              </div>
            </div>
          </header>

          <section className="w-full max-w-md bg-white shadow-xl rounded-2xl p-10">
            <VerificationMessage 
              email={verificationEmail} 
              onBack={() => {
                setVerificationEmail('');
                setIsLogin(true);
                setAcceptedTerms(false);
                setFormData({
                  email: '',
                  password: '',
                  firstName: '',
                  lastName: '',
                  phoneNumber: '',
                  skillLevel: '2.5'
                });
              }} 
            />
          </section>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen flex flex-col items-center justify-center bg-[#F8F8F8] px-4 pb-24">
      <div className="w-full max-w-md">
        {/* Header/Hero Section - Updated to match main page */}
        <header className="bg-primary shadow-sm mb-8 rounded-lg">
          <div className="px-6 py-8 text-center">
            <div className="flex flex-col items-center space-y-2">
              <div className="flex items-center gap-3">
                {/* Tennis ball icon */}
                <svg className="w-6 h-6 shrink-0" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  {/* Base circle with gradient */}
                  <circle cx="12" cy="12" r="11" fill="url(#tennis-gradient)" />
                  
                  {/* Tennis ball seam */}
                  <path
                    d="M12 1C5.925 1 1 5.925 1 12s4.925 11 11 11 11-4.925 11-11S18.075 1 12 1zm0 2c4.97 0 9 4.03 9 9s-4.03 9-9 9-9-4.03-9-9 4.03-9 9-9z"
                    fill="#E5B210"
                    fillOpacity="0.6"
                  />
                  
                  {/* S-curve seam */}
                  <path
                    d="M7 6.5C9.5 9 14.5 7 17 4.5M7 17.5c2.5-2.5 7.5-0.5 10 2"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  
                  {/* Gradient definition */}
                  <defs>
                    <linearGradient id="tennis-gradient" x1="12" y1="1" x2="12" y2="23" gradientUnits="userSpaceOnUse">
                      <stop stopColor="#FFB802" />
                      <stop offset="1" stopColor="#E5B210" />
                    </linearGradient>
                  </defs>
                </svg>
                <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight text-white">
                  Tennis<span className="text-accent">Now</span><span className="text-white">App.com</span>
                </h1>
              </div>
              <p className="text-white/80 text-sm md:text-base font-light">Connecting the Triangle's Tennis Community</p>
            </div>
          </div>
        </header>

        <section className="w-full max-w-md bg-white shadow-xl rounded-2xl p-10">
          <h2 className="text-2xl font-bold mb-8 text-center">
            {isLogin ? 'Sign in' : 'Create your account'}
          </h2>

          {/* Show submit error if any */}
          {errors.submit && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{errors.submit}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {!isLogin && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#334155] mb-2">
                      First Name
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-md border border-gray-300 
                               focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="First name"
                    />
                    {errors.firstName && (
                      <p className="mt-2 text-sm text-red-600">{errors.firstName}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#334155] mb-2">
                      Last Initial
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={(e) => handleInputChange({
                        target: {
                          name: 'lastName',
                          value: e.target.value.charAt(0).toUpperCase()
                        }
                      })}
                      className="w-full px-4 py-3 rounded-md border border-gray-300 
                               focus:outline-none focus:ring-2 focus:ring-primary text-center"
                      placeholder="Initial"
                      maxLength="1"
                    />
                    {errors.lastName && (
                      <p className="mt-2 text-sm text-red-600">{errors.lastName}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#334155] mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-md border border-gray-300 
                             focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Phone number"
                  />
                  {errors.phoneNumber && (
                    <p className="mt-2 text-sm text-red-600">{errors.phoneNumber}</p>
                  )}
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-[#334155] mb-2">
                Email Address
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full px-4 py-3 rounded-md border border-gray-300 
                         focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="you@example.com"
              />
              {errors.email && (
                <p className="mt-2 text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-[#334155] mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-md border border-gray-300 
                           focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-[#001427]"
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-2 text-sm text-red-600">{errors.password}</p>
              )}
            </div>

            {isLogin && (
              <div className="text-right">
                <button
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  className="text-sm text-primary hover:text-primaryDark hover:underline"
                >
                  Forgot your password?
                </button>
              </div>
            )}

            {!isLogin && (
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-2">
                  NTRP Rating
                </label>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(SKILL_LEVELS).map(([key, value]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => handleInputChange({ target: { name: 'skillLevel', value } })}
                      className={`px-4 py-2 rounded-lg border ${
                        formData.skillLevel === value
                          ? 'bg-primary/5 border-primary text-primary'
                          : 'border-gray-300 text-[#334155] hover:bg-gray-50'
                      }`}
                    >
                      {value}
                    </button>
                  ))}
                </div>
                {errors.skillLevel && (
                  <p className="mt-2 text-sm text-red-600">{errors.skillLevel}</p>
                )}
              </div>
            )}

            {!isLogin && (
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="acceptTerms"
                  checked={acceptedTerms}
                  onChange={(e) => {
                    setAcceptedTerms(e.target.checked);
                    // Clear terms error when user checks the box
                    if (errors.terms && e.target.checked) {
                      setErrors(prev => ({
                        ...prev,
                        terms: ''
                      }));
                    }
                  }}
                  className="mt-1 h-4 w-4 text-primary border-gray-300 rounded 
                           focus:ring-primary focus:ring-2"
                />
                <label htmlFor="acceptTerms" className="text-sm text-gray-600 leading-5">
                  I agree to the{' '}
                  <a
                    href="https://www.tennisnowapp.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:text-primaryDark hover:underline font-medium"
                  >
                    Terms and Conditions
                  </a>
                </label>
              </div>
            )}
            {!isLogin && errors.terms && (
              <p className="text-sm text-red-600 -mt-4">{errors.terms}</p>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-primary hover:bg-primaryDark text-white font-semibold 
                       rounded-lg py-3 mt-8 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {isLogin ? 'Signing in...' : 'Creating account...'}
                </div>
              ) : (
                isLogin ? 'Sign in' : 'Create account'
              )}
            </button>

            <p className="text-center text-sm text-[#334155] mt-8">
              {isLogin ? 'New here? ' : 'Already have an account? '}
              <button
                type="button"
                onClick={toggleMode}
                className="text-primary font-medium hover:underline"
              >
                {isLogin ? 'Create an account' : 'Sign in'}
              </button>
            </p>
          </form>
        </section>
      </div>
    </main>
  );
};

export default AuthForm; 